import React from 'react';
import { Box, Tooltip } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { EhiWindowSnapshots } from '@services/ehi.service.ts';

interface EhiTrendIndicatorProps {
  /** Вікно розрахунку і реальні знімки в ньому (з рядка узагальнених даних); null - запис ще не перераховано. */
  snapshots: EhiWindowSnapshots | null;
}

/**
 * Напрямок руху EHI: останній реальний знімок проти попереднього. Одне джерело і для стрілки, і для кольору числа EHI
 * (EhiSummariesTab), щоб вони не розходились. Зростання вважається позитивним (за вказівкою користувача).
 */
export const getEhiTrendDirection = (snapshots: EhiWindowSnapshots | null): 'up' | 'down' | null => {
  const items = snapshots?.items ?? [];
  if (items.length < 2) return null;
  const last = items[items.length - 1].value;
  const prev = items[items.length - 2].value;
  return last > prev ? 'up' : last < prev ? 'down' : null;
};

/** Колір напрямку: зростання - позитивне (success), падіння - error; без руху - звичайний колір тексту. */
export const EHI_TREND_COLOR = { up: 'success.main', down: 'error.main' } as const;

// Індикатор руху біля числа EHI (2026-09-22, за вказівкою користувача - "як на форексі": стрілка вгору, вниз чи без неї
// на базі 2 сусідніх значень). Порівнюються два ОСТАННІ реальні знімки з `windowSnapshots` (заповнює бекенд при
// перерахунку узагальнених даних). Однакові значення, менше 2 знімків або ще не перераховано - без стрілки.
// Підказка показує обидві дати й значення - так видно, що саме порівнюється, і чи був між знімками пропуск.
// Спершу тут була окрема колонка з міні-графіком за 30 днів - замінено цим індикатором після перегляду користувачем.
export const EhiTrendIndicator: React.FC<EhiTrendIndicatorProps> = ({ snapshots }) => {
  const { t, i18n } = useTranslation();
  const direction = getEhiTrendDirection(snapshots);
  if (!direction || !snapshots) return null;

  const { items } = snapshots;
  const last = items[items.length - 1];
  const prev = items[items.length - 2];
  const isUp = direction === 'up';
  const Icon = isUp ? ArrowDropUpIcon : ArrowDropDownIcon;
  const formatDate = (date: string) =>
    new Date(`${date}T00:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });

  return (
    <Tooltip
      title={t('ehiJournal.trendLastVsPrev', {
        prevDate: formatDate(prev.date),
        prev: prev.value,
        lastDate: formatDate(last.date),
        last: last.value,
      })}
    >
      <Box
        component="span"
        aria-label={t(isUp ? 'ehiJournal.trendUp' : 'ehiJournal.trendDown')}
        sx={{ display: 'inline-flex', color: EHI_TREND_COLOR[direction] }}
      >
        <Icon fontSize="small" />
      </Box>
    </Tooltip>
  );
};
